import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import { SkillLabelUpdateResponse } from '../../../../models';
import { CareerRoleAdminActionsService } from './career-role-admin-actions.service';

export interface RenameTopicDialogData {
  skillId: string;
  currentName: string;
}

@Component({
  selector: 'app-rename-topic-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressBarModule
  ],
  template: `
    <h2 mat-dialog-title>Rename topic</h2>
    <mat-progress-bar *ngIf="saving" mode="indeterminate"></mat-progress-bar>
    <form [formGroup]="form" (ngSubmit)="submit()">
      <mat-dialog-content>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Current name</mat-label>
          <input matInput formControlName="currentName" readonly />
        </mat-form-field>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>New name</mat-label>
          <input matInput formControlName="newName" />
        </mat-form-field>
        <p class="error" *ngIf="error">{{ error }}</p>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button type="button" (click)="close()" [disabled]="saving">Cancel</button>
        <button mat-flat-button color="primary" type="submit" [disabled]="form.invalid || saving">Save</button>
      </mat-dialog-actions>
    </form>
  `,
  styles: [
    `
      .full-width {
        width: 100%;
      }
      .error {
        color: #b00020;
        margin-top: 0.75rem;
      }
    `
  ]
})
export class RenameTopicDialogComponent {
  private readonly api = inject(CsrWebApiService);
  private readonly actions = inject(CareerRoleAdminActionsService);
  private readonly dialogRef = inject(MatDialogRef<RenameTopicDialogComponent, SkillLabelUpdateResponse | undefined>);
  private readonly data = inject<RenameTopicDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);

  saving = false;
  error = '';

  readonly form = this.fb.nonNullable.group({
    currentName: [{ value: this.data.currentName, disabled: true }, [Validators.required]],
    newName: ['', [Validators.required, Validators.minLength(1)]]
  });

  close(): void {
    this.dialogRef.close();
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.saving) {
      return;
    }
    const newName = this.form.controls.newName.value.trim();
    if (newName.localeCompare(this.data.currentName.trim(), undefined, { sensitivity: 'accent' }) === 0) {
      this.error = 'New name must differ from the current name.';
      return;
    }
    this.saving = true;
    this.error = '';
    try {
      const result = await firstValueFrom(
        this.api.patchSkillTopicName(this.data.skillId, {
          currentName: this.data.currentName,
          newName
        })
      );
      this.dialogRef.close(result);
    } catch (err) {
      this.error = this.actions.errorMessage(err);
    } finally {
      this.saving = false;
    }
  }
}
